import { useMemo, useState } from "react";
import { coverUrl } from "@/lib/cover";

interface P {
  coverId: string | null | undefined;
  alt: string;
  /** Fixed pixel size. If omitted, the cover fills its container fluidly (default). */
  size?: number;
  className?: string;
  /** Above-the-fold / LCP-critical image (page hero cover): loads eagerly
   * with high fetch priority instead of the default lazy+auto. Use sparingly
   * — one per page, not on shelf/grid thumbnails. */
  priority?: boolean;
}

const PALETTE = [
  "#5b21b6", "#7c3aed", "#a855f7", "#c026d3", "#db2777",
  "#e11d48", "#ea580c", "#d97706", "#65a30d", "#059669",
  "#0891b2", "#0284c7", "#2563eb", "#4f46e5",
];

function hashPick<T>(s: string, arr: T[]): T {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return arr[Math.abs(h) % arr.length];
}

export function CoverImg({ coverId, alt, size, className, priority }: P) {
  const [err, setErr] = useState(false);
  const url = coverUrl(coverId, size ?? 300);
  const fixed = size !== undefined;
  const initial = (alt || "?").trim().charAt(0).toUpperCase() || "?";
  const bg = useMemo(() => hashPick(alt || "?", PALETTE), [alt]);

  const baseStyle = fixed
    ? { width: size, height: size, minWidth: size, borderRadius: 6, objectFit: "cover" as const, display: "block" as const }
    : undefined;
  const cls = fixed ? (className ?? "") : `cover ${className ?? ""}`;

  if (!url || err) {
    // deterministic colored tile with the entity's first letter — no ugly grey blank
    return (
      <div
        className={cls}
        aria-label={alt}
        style={{
          ...baseStyle,
          background: `linear-gradient(135deg, ${bg} 0%, rgba(0,0,0,.35) 100%)`,
          color: "rgba(255,255,255,.9)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: fixed ? Math.max(14, (size ?? 300) * 0.42) : "clamp(24px, 30%, 64px)",
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
          userSelect: "none",
        }}
      >
        {initial}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      // @ts-expect-error fetchpriority isn't in the TS DOM lib's img attrs yet
      fetchpriority={priority ? "high" : undefined}
      decoding={priority ? "sync" : "async"}
      onError={() => setErr(true)}
      className={cls}
      style={{ ...baseStyle, background: bg }}
      width={fixed ? size : undefined}
      height={fixed ? size : undefined}
    />
  );
}
